// MBR128: the merchant can pick one theme colour (merchant setting `mbr_theme_color`).
// Every brand token in theme-tokens.css is derived from it here; with no colour set,
// nothing is written and the design palette stays in place.

const INK = "#110001"; // --mbr-color-ink-strong
const WHITE = "#FFFFFF";

function parseHex(value) {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value || "").trim());
  if (!match) return null;
  let hex = match[1];
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

function toHex(rgb) {
  return "#" + rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("").toUpperCase();
}

function mix(rgb, target, amount) {
  return rgb.map((c, i) => c + (target[i] - c) * amount);
}

function luminance(rgb) {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// Tokens to write on <html> for a chosen colour, or null when the colour is empty/invalid.
export function themeColorTokens(value) {
  const rgb = parseHex(value);
  if (!rgb) return null;
  const white = parseHex(WHITE);
  const ink = parseHex(INK);
  // Headings and the QR code sit on light surfaces: a colour too pale to read there falls back to ink.
  const readableOnLight = contrast(rgb, white) >= 3;
  const onLight = readableOnLight ? toHex(rgb) : INK;
  return {
    "--mbr-color-primary": toHex(rgb),
    "--mbr-color-primary-rgb": rgb.join(", "),
    "--mbr-color-primary-dark": toHex(mix(rgb, [0, 0, 0], 0.35)),
    "--mbr-color-primary-light": toHex(mix(rgb, [255, 255, 255], 0.1)),
    "--mbr-color-on-primary": contrast(rgb, white) >= contrast(rgb, ink) ? WHITE : INK,
    "--mbr-color-label": onLight,
    "--mbr-color-qr": onLight,
  };
}

export const THEME_COLOR_TOKEN_NAMES = Object.keys(themeColorTokens("#000000"));

// For props that need a real colour value (e.g. the QR code component), not a CSS var.
export function themeTokenValue(name, fallback) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}
